import { supabase } from '@/lib/supabase';
import type { BadgeRarity, TableRow } from '@/types/database';

type BadgeCatalog = TableRow<'badges_catalog'>;
type XpEvent = TableRow<'xp_events'>;

type BadgeDefinition = {
  key: string;
  name: string;
  icon: string;
  description: string;
  rarity: BadgeRarity;
  gemsReward: number;
  check: (stats: BadgeStats) => boolean;
};

type BadgeStats = {
  totalXp: number;
  level: number;
  streak: number;
  gemsBalance: number;
  badgesEarned: number;
  checkins: number;
  habitsCompleted: number;
  exercises: number;
  meals: number;
  waterLogs: number;
  breathingLogs: number;
  transactions: number;
  expenses: number;
  clients: number;
  wonLeads: number;
  paidInvoices: number;
  invoices: number;
  goalsCompleted: number;
  journalNotes: number;
  journalWords: number;
  roomsTouchedToday: number;
  actionsToday: number;
  actionsByRoomToday: Record<string, number>;
  createdAtHourMedian: number;
};

const BADGES: BadgeDefinition[] = [
  // Common
  def('first_light', 'First Light', '🌅', 'Complete your first check-in.', 'common', 3, (s) => s.checkins >= 1),
  def('pocket_change', 'Pocket Change', '💰', 'Log your first expense.', 'common', 3, (s) => s.expenses >= 1),
  def('ink_drop', 'Ink Drop', '✒️', 'Write your first journal note.', 'common', 3, (s) => s.journalNotes >= 1),
  def('open_for_business', 'Open for Business', '🏢', 'Add your first client.', 'common', 4, (s) => s.clients >= 1),
  def('first_invoice', 'First Invoice', '🧾', 'Create your first invoice.', 'common', 4, (s) => s.invoices >= 1),
  def('hydrated', 'Hydrated', '💧', 'Log water intake.', 'common', 2, (s) => s.waterLogs >= 1),
  def('first_breath', 'First Breath', '🫁', 'Complete a breathing session.', 'common', 2, (s) => s.breathingLogs >= 1),
  def('mission_accepted', 'Mission Accepted', '✅', 'Complete your first challenge.', 'common', 4, (s) => s.actionsToday >= 1),
  def('fortune_told', 'Fortune Told', '🔮', 'Reveal your daily tarot.', 'common', 2, (s) => (s.actionsByRoomToday.dashboard ?? 0) >= 1),
  def('spark', 'Spark', '⚡', 'Reach level 2.', 'common', 5, (s) => s.level >= 2),

  // Rare
  def('week_warrior', 'Week Warrior', '🛡️', 'Reach a 7-day streak.', 'rare', 10, (s) => s.streak >= 7),
  def('monthly_machine', 'Monthly Machine', '🗓️', 'Reach a 30-day streak.', 'rare', 15, (s) => s.streak >= 30),
  def('zen_master', 'Zen Master', '🧘', 'Complete 20 breathing sessions.', 'rare', 10, (s) => s.breathingLogs >= 20),
  def('frugal_fox', 'Frugal Fox', '🦊', 'Log 50 expenses mindfully.', 'rare', 12, (s) => s.expenses >= 50),
  def('data_nerd', 'Data Nerd', '📊', 'Log 100 transactions.', 'rare', 12, (s) => s.transactions >= 100),
  def('full_house', 'Full House', '🏠', 'Complete 25 habits.', 'rare', 10, (s) => s.habitsCompleted >= 25),
  def('wordsmith', 'Wordsmith', '📚', 'Write 10,000 total words.', 'rare', 15, (s) => s.journalWords >= 10000),
  def('deal_closer', 'Deal Closer', '🤝', 'Move your first lead to won.', 'rare', 12, (s) => s.wonLeads >= 1),
  def('first_blood', 'First Blood', '🩸', 'Get your first invoice paid.', 'rare', 12, (s) => s.paidInvoices >= 1),
  def('growth_spurt', 'Growth Spurt', '🌱', 'Reach level 5.', 'rare', 15, (s) => s.level >= 5),

  // Legendary
  def('centurion', 'Centurion', '💯', 'Reach a 100-day streak.', 'legendary', 30, (s) => s.streak >= 100),
  def('eternal', 'Eternal', '♾️', 'Reach a 365-day streak.', 'legendary', 50, (s) => s.streak >= 365),
  def('unicorn', 'Unicorn', '🦄', 'Reach level 12.', 'legendary', 35, (s) => s.level >= 12),
  def('full_house_master', 'Full House Master', '🏆', 'Complete 200 habits.', 'legendary', 25, (s) => s.habitsCompleted >= 200),
  def('goal_crusher', 'Goal Crusher', '🎯', 'Complete 5 savings goals.', 'legendary', 30, (s) => s.goalsCompleted >= 5),
  def('perfect_morning', 'Perfect Morning', '🌤️', 'Sustain a 30-day streak with regular check-ins.', 'legendary', 25, (s) => s.streak >= 30 && s.checkins >= 30),
  def('challenge_hunter', 'Challenge Hunter', '🗡️', 'Earn 20 badges.', 'legendary', 30, (s) => s.badgesEarned >= 20),

  // Hidden
  def('night_owl', 'Night Owl', '🌙', 'Log many late-night actions.', 'hidden', 10, (s) => s.createdAtHourMedian >= 22),
  def('phoenix', 'Phoenix', '🔥', 'Return after a break and keep going.', 'hidden', 10, (s) => s.checkins >= 10 && s.streak >= 3),
  def('combo_king', 'Combo King', '👑', 'Trigger 10 combo events.', 'hidden', 15, (s) => (s.actionsByRoomToday.combos ?? 0) >= 10),
  def('deep_diver', 'Deep Diver', '🌊', 'Do 50 actions in Journal.', 'hidden', 10, (s) => (s.actionsByRoomToday.journal_total ?? 0) >= 50),
  def('lucky_star', 'Lucky Star', '🌟', 'Hit 3 legendary drops.', 'hidden', 15, (s) => (s.actionsByRoomToday.legendaryDrops ?? 0) >= 3),
  def('self_aware', 'Self-Aware', '🪞', 'Write 100 journal notes.', 'hidden', 12, (s) => s.journalNotes >= 100),
  def('gem_hoarder', 'Gem Hoarder', '💎', 'Hold 500 gems.', 'hidden', 15, (s) => s.gemsBalance >= 500),
  def('early_bird', 'Early Bird', '🌄', 'Create many morning actions.', 'hidden', 10, (s) => s.createdAtHourMedian <= 7),
];

export async function evaluateAndAwardBadges(userId: string) {
  const stats = await buildBadgeStats(userId);
  const existing = await supabase.from('badges_earned').select('badge_id').eq('user_id', userId);
  const earnedBadgeIds = new Set((existing.data ?? []).map((entry) => entry.badge_id));

  const catalog = await supabase.from('badges_catalog').select('*');
  const catalogByName = new Map<string, BadgeCatalog>(
    (catalog.data ?? []).map((entry) => [entry.name, entry]),
  );

  const newlyEarned: Array<{ name: string; gems: number }> = [];

  for (const badge of BADGES) {
    if (!badge.check(stats)) continue;

    const catalogEntry = catalogByName.get(badge.name);
    if (!catalogEntry) continue;
    if (earnedBadgeIds.has(catalogEntry.id)) continue;

    await supabase.from('badges_earned').upsert(
      { user_id: userId, badge_id: catalogEntry.id, earned_at: new Date().toISOString() },
      { onConflict: 'user_id,badge_id' },
    );

    if (badge.gemsReward > 0) {
      const gs = await supabase.from('game_state').select('gems_balance').eq('user_id', userId).single();
      const nextBalance = (gs.data?.gems_balance ?? 0) + badge.gemsReward;
      await supabase.from('gem_transactions').insert({
        user_id: userId,
        amount: badge.gemsReward,
        reason: `badge_${badge.key}`,
        balance_after: nextBalance,
      });
    }
    earnedBadgeIds.add(catalogEntry.id);
    newlyEarned.push({ name: badge.name, gems: badge.gemsReward });
  }

  return newlyEarned;
}

export async function processCombosAndDrops(userId: string, action: string, room: string | null) {
  const today = localDate();
  const todayEventsRes = await supabase
    .from('xp_events')
    .select('*')
    .eq('user_id', userId)
    .gte('created_at', `${today}T00:00:00`)
    .lt('created_at', `${today}T23:59:59`);

  const todayEvents = todayEventsRes.data ?? [];

  const combos: Array<{ key: string; name: string; xp: number; ok: boolean }> = [
    {
      key: 'mood_master',
      name: 'Mood Master',
      xp: 20,
      ok: await hasMoodMaster(userId, today),
    },
    {
      key: 'health_trifecta',
      name: 'Health Trifecta',
      xp: 30,
      ok: await hasHealthTrifecta(userId, today),
    },
    {
      key: 'business_momentum',
      name: 'Business Momentum',
      xp: 25,
      ok: await hasBusinessMomentum(userId, today),
    },
    {
      key: 'full_spectrum',
      name: 'Full Spectrum',
      xp: 40,
      ok: hasFullSpectrum(todayEvents),
    },
    {
      key: 'deep_dive',
      name: 'Deep Dive',
      xp: 20,
      ok: hasDeepDive(todayEvents),
    },
  ];

  const triggered: string[] = [];
  for (const combo of combos) {
    if (!combo.ok) continue;
    const comboAction = `combo_${combo.key}`;
    const exists = todayEvents.some((entry) => entry.action === comboAction);
    if (exists) continue;
    await supabase.from('xp_events').insert({
      user_id: userId,
      action: comboAction,
      xp_amount: combo.xp,
      room: room ?? null,
      multiplier: 1,
      source: 'combo',
    });
    triggered.push(combo.name);
  }

  const drop = await maybeDropGems(userId, action);
  return { triggeredCombos: triggered, drop };
}

async function maybeDropGems(userId: string, action: string) {
  const significant = !action.startsWith('combo_') && !action.startsWith('level_');
  if (!significant) return null;
  const seed = `${userId}:${Date.now()}:${action}`;
  const roll = seededFraction(seed);
  let amount = 0;
  let reason = 'normal_drop';
  if (roll < 0.02) {
    amount = randomInt(15, 25, `${seed}:legendary`);
    reason = 'legendary_drop';
  } else if (roll < 0.10) {
    amount = randomInt(5, 10, `${seed}:rare`);
    reason = 'rare_drop';
  } else {
    amount = randomInt(1, 3, `${seed}:normal`);
  }
  const gs = await supabase.from('game_state').select('gems_balance').eq('user_id', userId).single();
  const next = (gs.data?.gems_balance ?? 0) + amount;
  await supabase.from('gem_transactions').insert({
    user_id: userId,
    amount,
    reason,
    balance_after: next,
  });
  return { amount, reason };
}

export async function ensureDailyChallengeForUser(userId: string) {
  const today = localDate();
  const existing = await supabase
    .from('challenges')
    .select('id')
    .eq('user_id', userId)
    .eq('type', 'daily')
    .gte('starts_at', `${today}T00:00:00`)
    .lt('starts_at', `${today}T23:59:59`)
    .maybeSingle();
  if (existing.data?.id) return;
  const templates = await supabase.from('challenge_templates').select('*').eq('is_active', true);
  const template = pickTemplateWeighted(templates.data ?? []);
  if (!template) return;
  const starts = `${today}T00:00:00.000Z`;
  const ends = `${today}T23:59:59.000Z`;
  await supabase.from('challenges').insert({
    user_id: userId,
    template_id: template.id,
    title: template.title,
    description: template.description,
    type: 'daily',
    status: 'active',
    target_value: template.target_value,
    current_value: 0,
    xp_reward: 60,
    gem_reward: 3,
    room: template.room,
    starts_at: starts,
    ends_at: ends,
    completed_at: null,
  });
}

function pickTemplateWeighted(templates: TableRow<'challenge_templates'>[]) {
  if (templates.length === 0) return null;
  const idx = Math.floor(Math.random() * templates.length);
  return templates[idx] ?? null;
}

async function buildBadgeStats(userId: string): Promise<BadgeStats> {
  const today = localDate();
  const [gameState, checkins, habitCompletions, exercise, meals, metrics, tx, clients, leads, invoices, goals, notes, earned, xp, gems] = await Promise.all([
    supabase.from('game_state').select('*').eq('user_id', userId).maybeSingle(),
    supabase.from('daily_checkins').select('id').eq('user_id', userId),
    supabase.from('habit_completions').select('id').eq('user_id', userId),
    supabase.from('exercise_log').select('id').eq('user_id', userId),
    supabase.from('meal_log').select('id').eq('user_id', userId),
    supabase.from('health_metrics').select('water_intake').eq('user_id', userId),
    supabase.from('transactions').select('id,type').eq('user_id', userId),
    supabase.from('clients').select('id').eq('user_id', userId).eq('is_active', true),
    supabase.from('pipeline_leads').select('id,stage').eq('user_id', userId),
    supabase.from('invoices').select('id,status').eq('user_id', userId),
    supabase.from('savings_goals').select('id,is_completed').eq('user_id', userId),
    supabase.from('notes').select('id,type,content,created_at').eq('user_id', userId),
    supabase.from('badges_earned').select('id').eq('user_id', userId),
    supabase.from('xp_events').select('*').eq('user_id', userId),
    supabase.from('gem_transactions').select('amount').eq('user_id', userId),
  ]);

  const gs = gameState.data;
  const xpEvents = (xp.data ?? []) as XpEvent[];
  const todayEvents = xpEvents.filter((entry) => entry.created_at.startsWith(today));
  const actionsByRoomToday: Record<string, number> = {};
  for (const entry of todayEvents) {
    const key = entry.room ?? 'none';
    actionsByRoomToday[key] = (actionsByRoomToday[key] ?? 0) + 1;
    if (entry.action.startsWith('combo_')) actionsByRoomToday.combos = (actionsByRoomToday.combos ?? 0) + 1;
  }
  const legendaryDrops = (gems.data ?? []).filter((entry) => entry.amount > 0).length;
  const journalNotes = (notes.data ?? []).filter((entry) => entry.type === 'journal');
  const actionHours = (xpEvents.map((entry) => new Date(entry.created_at).getHours())).sort((a, b) => a - b);
  const medianHour = actionHours.length === 0 ? 12 : actionHours[Math.floor(actionHours.length / 2)]!;

  return {
    totalXp: gs?.total_xp ?? 0,
    level: gs?.current_level ?? 1,
    streak: gs?.current_streak ?? 0,
    gemsBalance: gs?.gems_balance ?? 0,
    badgesEarned: (earned.data ?? []).length,
    checkins: (checkins.data ?? []).length,
    habitsCompleted: (habitCompletions.data ?? []).length,
    exercises: (exercise.data ?? []).length,
    meals: (meals.data ?? []).length,
    waterLogs: (metrics.data ?? []).filter((entry) => (entry.water_intake ?? 0) > 0).length,
    breathingLogs: xpEvents.filter((entry) => entry.action === 'health_breathing').length,
    transactions: (tx.data ?? []).length,
    expenses: (tx.data ?? []).filter((entry) => entry.type === 'expense').length,
    clients: (clients.data ?? []).length,
    wonLeads: (leads.data ?? []).filter((entry) => entry.stage === 'won').length,
    paidInvoices: (invoices.data ?? []).filter((entry) => entry.status === 'paid').length,
    invoices: (invoices.data ?? []).length,
    goalsCompleted: (goals.data ?? []).filter((entry) => entry.is_completed).length,
    journalNotes: journalNotes.length,
    journalWords: journalNotes.reduce((sum, entry) => sum + countWords(entry.content), 0),
    roomsTouchedToday: new Set(todayEvents.map((entry) => entry.room).filter(Boolean)).size,
    actionsToday: todayEvents.length,
    actionsByRoomToday: {
      ...actionsByRoomToday,
      journal_total: xpEvents.filter((entry) => entry.room === 'journal').length,
      legendaryDrops,
    },
    createdAtHourMedian: medianHour,
  };
}

async function hasMoodMaster(userId: string, date: string) {
  const moods = await supabase
    .from('mood_entries')
    .select('id')
    .eq('user_id', userId)
    .gte('created_at', `${date}T00:00:00`)
    .lt('created_at', `${date}T23:59:59`);
  return (moods.data ?? []).length >= 3;
}

async function hasHealthTrifecta(userId: string, date: string) {
  const [habits, exercise, meals] = await Promise.all([
    supabase.from('habit_completions').select('id').eq('user_id', userId).eq('date', date).limit(1),
    supabase.from('exercise_log').select('id').eq('user_id', userId).eq('date', date).limit(1),
    supabase.from('meal_log').select('id').eq('user_id', userId).eq('date', date).limit(1),
  ]);
  return Boolean(habits.data?.length && exercise.data?.length && meals.data?.length);
}

async function hasBusinessMomentum(userId: string, date: string) {
  const [expense, invoice] = await Promise.all([
    supabase.from('transactions').select('id').eq('user_id', userId).eq('type', 'expense').eq('date', date).limit(1),
    supabase.from('invoices').select('id').eq('user_id', userId).in('status', ['sent', 'overdue', 'paid']).gte('updated_at', `${date}T00:00:00`).lt('updated_at', `${date}T23:59:59`).limit(1),
  ]);
  return Boolean(expense.data?.length && invoice.data?.length);
}

function hasFullSpectrum(todayEvents: XpEvent[]) {
  const rooms = new Set(todayEvents.map((entry) => entry.room));
  return ['dashboard', 'health', 'wealth', 'business', 'journal'].every((room) => rooms.has(room));
}

function hasDeepDive(todayEvents: XpEvent[]) {
  const byRoom: Record<string, number> = {};
  for (const entry of todayEvents) {
    if (!entry.room) continue;
    byRoom[entry.room] = (byRoom[entry.room] ?? 0) + 1;
  }
  return Object.values(byRoom).some((count) => count >= 5);
}

function def(
  key: string,
  name: string,
  icon: string,
  description: string,
  rarity: BadgeRarity,
  gemsReward: number,
  check: (stats: BadgeStats) => boolean,
): BadgeDefinition {
  return { key, name, icon, description, rarity, gemsReward, check };
}

function localDate(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function countWords(value: string) {
  const text = value.trim();
  if (!text) return 0;
  return text.split(/\s+/).length;
}

function seededFraction(seed: string) {
  let hash = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  const normalized = (hash >>> 0) / 4294967295;
  return normalized;
}

function randomInt(min: number, max: number, seed: string) {
  const fraction = seededFraction(seed);
  return Math.floor(fraction * (max - min + 1)) + min;
}

